// GridFooterWithInlineForm.js
import React from "react";
import { Box, IconButton, Collapse, Divider, Tooltip, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { GridFooterContainer, GridPagination } from "@mui/x-data-grid";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  AddBox  as AddIcon,
} from "@mui/icons-material";

/**
 * MUI X DataGrid v5 custom Footer component.
 * Receives your props via DataGrid: componentsProps={{ footer: {...} }}
 */
export default function GridFooterWithInlineForm(props) {
  const {
    footerRef,
    openInlineForm,
    closeForm,
    isEditMode,
    editForm,
    subFormErrors,
    isSubmitting,
    handleSubmit,
    handleSubmitAndNew,
    handleDeleteCurrent,
  } = props;

  const hasErrors = !!subFormErrors && Object.keys(subFormErrors).length > 0;

  // When closed: show ONLY pagination (prevents empty footer space)
  if (!openInlineForm) {
    return (
      <GridFooterContainer ref={footerRef} sx={{ minHeight: 0, p: 0 }}>
        <GridPagination />
      </GridFooterContainer>
    );
  }

  // If in edit mode, show the form in a modal dialog
  if (isEditMode) {
    return (
      <>
      <GridFooterContainer ref={footerRef} sx={{ minHeight: 0, p: 0 }}>
        <GridPagination />
      </GridFooterContainer>
      <Dialog
        open={openInlineForm}
        onClose={closeForm}
        fullWidth={true}
        maxWidth={false}
        scroll="body"
        hideBackdrop={false}
        disableEnforceFocus
        disableScrollLock
        PaperProps={{
        sx: {
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          maxWidth: "100%",
          margin: 0,
          maxHeight: "50vh",
        },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            px: 3,
            pt: 2,
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ fontWeight: 700 }}>Edit record</Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Tooltip title="Cancel" arrow>
              <IconButton onClick={closeForm} color="warning" size="small">
                <CloseIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <IconButton
                onClick={handleDeleteCurrent}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Submit" arrow>
              <IconButton
                onClick={handleSubmit}
                disabled={isSubmitting || hasErrors}
                color="primary"
              >
                <SaveIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
        <Box sx={{ mt: 2, mb: 2 }}>{editForm}</Box>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  // When adding, show form above pagination (inline)
  return (
    <GridFooterContainer
      ref={footerRef}
      sx={{ display: "block", minHeight: 0, p: 0 }}
    >
      <Collapse
        in={openInlineForm}
        timeout="auto"
        unmountOnExit
        sx={{
          bgcolor: "action.hover",
          transition: (theme) =>
            theme.transitions.create("background-color", {
              duration: theme.transitions.duration.short,
            }),
        }}
      >
        <Box sx={{ px: 2, py: 2, borderTop: 1, borderColor: "divider" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ fontWeight: 700 }}>Add new record</Box>
            <Box
              sx={{
                mt: 2,
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Tooltip title="Cancel" arrow>
                <IconButton onClick={closeForm} color="warning" size="small">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Submit" arrow>
                <IconButton
                  onClick={handleSubmit}
                  disabled={isSubmitting || hasErrors}
                  color="primary"
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Submit & New" arrow>
                <IconButton
                  onClick={handleSubmitAndNew}
                  disabled={isSubmitting || hasErrors}
                  color="primary"
                >
                  <SaveIcon />
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ mt: 2, mb: 2 }}>{editForm}</Box>
        </Box>
      </Collapse>
      <Divider />
      <Divider />
      <Divider />
      <Divider />
      <GridPagination />
    </GridFooterContainer>
  );
}
